import { useMemo, useState } from 'react'
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { TooltipContentProps } from 'recharts/types/component/Tooltip'

import * as api from 'api/api'
import { DisplayStatus } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { Card, EmptyState, ErrorState, SelectField, Spinner } from 'components/ui'
import { STATUS_LABELS, STATUS_ORDER, addDays, formatDate } from 'lib/format'
import { useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'
import { ForbiddenPage } from 'pages/system/ForbiddenPage'

const STATUS_COLOR: Record<DisplayStatus, string> = {
  COMPLETE: 'var(--chart-approved)',
  CREATED: 'var(--chart-submitted)',
  DRAFT: 'var(--chart-draft)',
  MISSING: 'var(--chart-overdue)',
  NEW: 'var(--new, #94a3b8)',
}

const DonutTooltip = ({ active, payload }: TooltipContentProps) => {
  if (!active || !payload?.length) return null
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip__row">
        <span className="chart-tooltip__label">{payload[0].name}</span>
        <strong>{payload[0].value}</strong>
      </div>
    </div>
  )
}

const DeltaTag = ({ value, unit = '%', goodDirection = 'up' }: { value?: number | null; unit?: string; goodDirection?: 'up' | 'down' }) => {
  if (value == null) return null
  const good = goodDirection === 'up' ? value >= 0 : value <= 0
  return (
    <span className={`small ${good ? 'plan-ahead' : 'plan-behind'}`}>
      {value > 0 ? '+' : ''}
      {value}
      {unit === '%' ? '%' : t(' نقطة', ' pts')}
      {t(' مقابل الفترة السابقة', ' vs the previous period')}
    </span>
  )
}

/** Portfolio-wide analytics — admin console, all sites (Figma: Insights / Analytics). Every chart is derived from
 *  real DailyReport data across the selected scope; "Issues by category" from the Figma frame is omitted since no
 *  issue-tracking entity exists in this app, and "on-time" means submitted the same calendar day as the report
 *  date rather than a fixed 18:00 cutoff the app doesn't model. */
export const AnalyticsPortfolioPage = () => {
  const { user } = useAuth()
  const [rangeDays, setRangeDays] = useState(30)
  const [projectId, setProjectId] = useState('')
  const { data: today } = useAsync(api.fetchToday, [])
  const { data: projects } = useAsync(api.fetchProjects, [])

  const to = today
  const from = today ? addDays(today, -(rangeDays - 1)) : undefined

  const { data: analytics, error, loading, reload } = useAsync(
    () => (from && to ? api.fetchPortfolioAnalytics(from, to, projectId || undefined) : Promise.resolve(undefined)),
    [from, to, projectId]
  )

  const reportsTrend = useMemo(
    () => (analytics?.reportsPerDay ?? []).map((point) => ({ label: formatDate(point.date).slice(0, 5), value: point.value })),
    [analytics]
  )
  const manpowerTrend = useMemo(
    () => (analytics?.manpowerPerDay ?? []).map((point) => ({ label: formatDate(point.date).slice(0, 5), value: point.value })),
    [analytics]
  )
  const statusData = STATUS_ORDER.map((status) => ({
    name: STATUS_LABELS[status],
    status,
    value: analytics?.statusSplit.find((item) => item.status === status)?.count ?? 0,
  })).filter((item) => item.value > 0)
  const rangeOptions = [
    { days: 7, label: t('آخر 7 أيام', 'Last 7 days') },
    { days: 30, label: t('آخر 30 يوماً', 'Last 30 days') },
    { days: 90, label: t('آخر 90 يوماً', 'Last 90 days') },
  ]

  if (!user?.isAdmin) return <ForbiddenPage message={t('التحليلات العامة متاحة لمدير النظام فقط.', 'Portfolio analytics is available to admins only.')} />

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('التحليلات', 'Analytics')}</h1>
          <p className="muted">{t('كل المشاريع · من التقارير المُرسلة والمعتمدة', 'All projects · from submitted and approved reports')}</p>
        </div>
        <div className="toolbar">
          <SelectField label={t('الفترة', 'Period')} value={rangeDays} onChange={(event) => setRangeDays(Number(event.target.value))}>
            {rangeOptions.map((option) => (
              <option key={option.days} value={option.days}>
                {option.label}
              </option>
            ))}
          </SelectField>
          <SelectField label={t('الموقع', 'Site')} value={projectId} onChange={(event) => setProjectId(event.target.value)}>
            <option value="">{t('كل المواقع', 'All sites')}</option>
            {projects?.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </SelectField>
        </div>
      </div>

      {loading && !analytics ? (
        <Spinner />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : !analytics ? null : (
        <div className="summary-grid">
          <Card title={t('التقارير المُرسلة يومياً', 'Reports submitted per day')} className="span-2">
            <div className="status-chart__plot">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={reportsTrend} margin={{ bottom: 0, left: 0, right: 0, top: 8 }}>
                  <CartesianGrid stroke="var(--chart-grid, #e5e7eb)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: 'var(--ink-2)', fontSize: 11 }} tickLine={false} axisLine={{ stroke: 'var(--line)' }} />
                  <YAxis allowDecimals={false} tick={{ fill: 'var(--ink-2)', fontSize: 12 }} tickLine={false} axisLine={false} width={24} />
                  <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)' }} />
                  <Line type="monotone" dataKey="value" name={t('تقارير مُرسلة', 'Reports submitted')} stroke="var(--chart-1, var(--brand))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="muted small">
              {t(`${analytics.reportsSubmittedTotal} تقريراً مُرسلاً`, `${analytics.reportsSubmittedTotal} reports submitted`)} <DeltaTag value={analytics.reportsSubmittedDeltaPct} />
            </p>
          </Card>

          <Card title={t('اتجاه الكوادر', 'Crew trend')}>
            <div className="status-chart__plot">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={manpowerTrend} margin={{ bottom: 0, left: 0, right: 0, top: 8 }}>
                  <CartesianGrid stroke="var(--chart-grid, #e5e7eb)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: 'var(--ink-2)', fontSize: 11 }} tickLine={false} axisLine={{ stroke: 'var(--line)' }} />
                  <YAxis allowDecimals={false} tick={{ fill: 'var(--ink-2)', fontSize: 12 }} tickLine={false} axisLine={false} width={32} />
                  <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)' }} />
                  <Line type="monotone" dataKey="value" name={t('الكوادر', 'Crew')} stroke="var(--chart-2)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="muted small">
              {t(`متوسط ${analytics.manpowerAvg} كادر/يوم`, `Average ${analytics.manpowerAvg} workers/day`)} <DeltaTag value={analytics.manpowerDeltaPct} />
            </p>
          </Card>

          <Card title={t('استخدام المعدات (يوم-آلة)', 'Equipment use (machine-days)')}>
            {analytics.equipmentUsage.length ? (
              <>
                <div className="status-chart__plot">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={analytics.equipmentUsage} margin={{ bottom: 0, left: 0, right: 0, top: 8 }}>
                      <CartesianGrid stroke="var(--chart-grid, #e5e7eb)" vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: 'var(--ink-2)', fontSize: 11 }} tickLine={false} axisLine={{ stroke: 'var(--line)' }} />
                      <YAxis allowDecimals={false} tick={{ fill: 'var(--ink-2)', fontSize: 12 }} tickLine={false} axisLine={false} width={24} />
                      <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)' }} />
                      <Bar dataKey="unitDays" name={t('يوم-آلة', 'Machine-days')} fill="var(--chart-2, var(--brand))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="muted small">
                  <DeltaTag value={analytics.equipmentDeltaPct} goodDirection="up" />
                </p>
              </>
            ) : (
              <EmptyState title={t('لا توجد معدات مسجّلة في هذه الفترة', 'No equipment recorded in this period')} />
            )}
          </Card>

          <Card title={t('نسبة الإرسال في نفس اليوم', 'Same-day submission rate')}>
            <div className="status-chart__plot">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={[
                      { name: t('في الوقت', 'On time'), value: analytics.onTimeCount },
                      { name: t('متأخر/لم يُرسل', 'Late or not submitted'), value: Math.max(0, analytics.dueCount - analytics.onTimeCount) },
                    ]}
                    dataKey="value"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    <Cell fill="var(--chart-approved)" />
                    <Cell fill="var(--line)" />
                  </Pie>
                  <Tooltip content={DonutTooltip} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <p className="muted small" style={{ textAlign: 'center' }}>
              {analytics.onTimeRate != null ? `${Math.round(analytics.onTimeRate * 100)}%` : '—'} · {analytics.onTimeCount} {t('من', 'of')}{' '}
              {analytics.dueCount} <DeltaTag value={analytics.onTimeDeltaPts} unit="pts" />
            </p>
          </Card>

          <Card title={t('توزيع حالات التقارير', 'Report status breakdown')}>
            {statusData.length ? (
              <>
                <div className="status-chart__plot">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80} paddingAngle={2}>
                        {statusData.map((item) => (
                          <Cell key={item.status} fill={STATUS_COLOR[item.status]} />
                        ))}
                      </Pie>
                      <Tooltip content={DonutTooltip} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="status-chart__legend">
                  {statusData.map((item) => (
                    <span key={item.status}>
                      <span className="status-chart__swatch" style={{ background: STATUS_COLOR[item.status] }} />
                      {item.name} · {item.value}
                    </span>
                  ))}
                </div>
                <p className="muted small" style={{ textAlign: 'center' }}>
                  {t(`${analytics.totalReports} تقريراً في الفترة`, `${analytics.totalReports} reports in this period`)}
                </p>
              </>
            ) : (
              <EmptyState title={t('لا توجد تقارير في هذه الفترة', 'No reports in this period')} />
            )}
          </Card>
        </div>
      )}
    </div>
  )
}
